import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import DownloadIcon from '../../assets/Icons/download-icon';
import ClockIcon from '../../assets/Icons/clock-icon';
import VisibilityIcon from '../../assets/Icons/visibility-icon';
import FileIcon from '../../assets/Icons/file-icon';
import { CustomizedSwitch } from '../../components/UI';
import { format } from 'date-fns';
import TrashIcon from '../../assets/Icons/trash';
import { ReactNode, useMemo, useState } from 'react';
import Dots from '../../assets/Icons/dots-three';

type ActionProps = {
  icon: JSX.Element;
  text: string;
  onClick?: () => void;
};

type DocumentProps = {
  icon?: ReactNode;
  title: string;
  toggleVisibility?: boolean;
  visible?: boolean;
  uploadedAt?: string;
  square?: boolean;
  delete?: boolean;
  download?: boolean;
  display?: boolean;
  actions?: ActionProps[];
  onChangeVisibility?: (visible: boolean) => void;
  onDownload?: () => void;
  onDisplay?: () => void;
  onDelete?: () => void;
};

type DocumentIcons = 'download' | 'display' | 'delete';

export const Document = ({
  icon,
  title,
  visible,
  toggleVisibility = false,
  uploadedAt,
  square,
  onChangeVisibility,
  onDownload,
  onDisplay,
  onDelete,
  delete: deleteDocument,
  download,
  display,
  actions,
}: DocumentProps) => {
  const [loading, setLoading] = useState({
    download: false,
    display: false,
    delete: false,
  });
  const [openActions, setOpenActions] = useState<HTMLButtonElement | null>(
    null
  );

  const handleMethods = async (
    iconName: DocumentIcons,
    method?: () => void
  ) => {
    const newLoading = { ...loading };
    if (!!method) {
      newLoading[iconName] = true;
      setLoading(newLoading);
      try {
        await method();
      } finally {
        newLoading[iconName] = false;
        setLoading(newLoading);
      }
    }
  };

  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenActions(event.currentTarget);
  };

  const handleCloseActions = () => {
    setOpenActions(null);
  };

  const actionsButton = !!actions && (
    <Box>
      <IconButton
        aria-label='close'
        sx={{
          padding: 0,
          color: theme => theme.palette.grey[500],
        }}
        onClick={handleOpenActions}
      >
        <Dots />
      </IconButton>

      <Menu
        id='basic-menu'
        anchorEl={openActions}
        open={Boolean(openActions)}
        onClose={handleCloseActions}
        // MenuListProps={{
        //   'aria-labelledby': 'basic-button',
        // }}
      >
        {actions?.map((action, index) => {
          return (
            <MenuItem key={index} onClick={action.onClick}>
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.text}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );

  const icons = useMemo(() => {
    const documentIcons: any = {
      download: {
        icon: <DownloadIcon />,
        method: onDownload,
        show: !!download,
      },
      display: { icon: <VisibilityIcon />, method: onDisplay, show: !!display },
      delete: { icon: <TrashIcon />, method: onDelete, show: !!deleteDocument },
    };

    const filteredIcons = Object.keys(documentIcons).filter(
      icon => documentIcons[icon].show
    );

    const content = filteredIcons.map((iconName: string) => {
      const icon = iconName as DocumentIcons;

      return (
        <IconButton
          key={iconName}
          sx={{
            padding: 0,
            color: theme => theme.palette.grey[500],
          }}
          onClick={() => handleMethods(icon, documentIcons[icon].method)}
        >
          {!!loading[icon] ? (
            <CircularProgress size={20} />
          ) : (
            documentIcons[icon].icon
          )}
        </IconButton>
      );
    });

    return (
      <Box sx={{ display: 'flex', gap: '24px' }}>
        {content}
        {actionsButton}
      </Box>
    );
  }, [deleteDocument, display, download, openActions]);

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        display: 'flex',
        padding: '12px 24px',
        borderRadius: !!square ? '4px' : '16px',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Stack
        width='400px'
        direction='row'
        display='flex'
        gap='16px'
        alignItems='center'
      >
        <Box>{icon || <FileIcon />}</Box>
        <Tooltip title={title}>
          <Typography
            sx={{
              color: 'var(--Primary-Text, #202A2D)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              cursor: 'default',
            }}
          >
            {title}
          </Typography>
        </Tooltip>
      </Stack>

      {!!uploadedAt && (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='center'
          gap={'8px'}
        >
          <ClockIcon />
          <Typography
            sx={{
              color: 'var(--Secondary-Text, #655C68)',
              fontFamily: 'Open Sans',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              cursor: 'default',
            }}
          >
            {format(new Date(uploadedAt), 'MM/dd/yyyy HH:mm')}
          </Typography>
        </Stack>
      )}

      <Stack direction='row' alignItems='center'>
        <Box sx={{ display: 'flex', gap: '36px' }}>
          {icons}
          {!!toggleVisibility && (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <CustomizedSwitch
                checked={visible}
                onClick={onChangeVisibility}
              />
              <Typography
                sx={{
                  color: '#202A2D',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                }}
              >
                Display on Client account
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
